import type { CreateSubmissionInput, Submission } from '@jemmi/schemas';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { apiRequest } from '@/lib/apiClient';

const SUBMISSIONS_KEY = ['submissions'] as const;

export function useChallengeSubmissions(challengeId: string | undefined) {
  return useQuery({
    queryKey: [...SUBMISSIONS_KEY, 'challenge', challengeId],
    queryFn: () => apiRequest<{ items: Submission[] }>(`/submissions/challenge/${challengeId}`),
    enabled: Boolean(challengeId),
  });
}

export function useMySubmission(challengeId: string | undefined, signedIn: boolean) {
  return useQuery({
    queryKey: [...SUBMISSIONS_KEY, 'mine', challengeId],
    queryFn: () => apiRequest<{ submission: Submission | null }>(`/submissions/mine/${challengeId}`),
    enabled: Boolean(challengeId) && signedIn,
    retry: false,
  });
}

export function useCreateSubmission() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateSubmissionInput) =>
      apiRequest<{ submission: Submission }>('/submissions', {
        method: 'POST',
        body: JSON.stringify(input),
      }),
    onSuccess: ({ submission }) => {
      void queryClient.invalidateQueries({ queryKey: [...SUBMISSIONS_KEY, 'challenge', submission.challengeId] });
      void queryClient.invalidateQueries({ queryKey: [...SUBMISSIONS_KEY, 'mine', submission.challengeId] });
      void queryClient.invalidateQueries({ queryKey: ['challenges', submission.challengeId] });
      void queryClient.invalidateQueries({ queryKey: ['challenges'] });
    },
  });
}

export function useReplaceSubmission(challengeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: Omit<CreateSubmissionInput, 'challengeId'> }) =>
      apiRequest<{ submission: Submission }>(`/submissions/${id}`, {
        method: 'PUT',
        body: JSON.stringify(input),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...SUBMISSIONS_KEY, 'challenge', challengeId] });
      void queryClient.invalidateQueries({ queryKey: [...SUBMISSIONS_KEY, 'mine', challengeId] });
      void queryClient.invalidateQueries({ queryKey: ['challenges', challengeId] });
      void queryClient.invalidateQueries({ queryKey: ['challenges'] });
    },
  });
}

export function useDeleteSubmission(challengeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiRequest<{ deleted: boolean }>(`/submissions/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: [...SUBMISSIONS_KEY, 'challenge', challengeId] });
      void queryClient.invalidateQueries({ queryKey: [...SUBMISSIONS_KEY, 'mine', challengeId] });
      void queryClient.invalidateQueries({ queryKey: ['challenges', challengeId] });
      void queryClient.invalidateQueries({ queryKey: ['challenges'] });
    },
  });
}
